import Fuse from 'fuse.js';
import { DICTIONARY_FUSE_THRESHOLD } from '../../constants/enum';
import { DictionaryMatchResult, DictionaryRow } from '../../types/dictionary';

// 辞書照合の表記ゆれを吸収するための正規化。
// 全角半角（NFKC）・大文字小文字・空白の違いを無視して一致させる。
//   例: 「１５％OFF」「15％ＯＦＦ」「15% off」→ いずれも "15%off"
export const normalizeForDictionarySearch = (value: string): string =>
  value.normalize('NFKC').toLowerCase().replace(/\s+/g, ''); // \s は全角スペース(U+3000)も含む

// fuse.js のスコア（0=完全一致, 1=不一致）と「文字数の差」から一致度(0〜1)を算出する。
// ignoreLocation:true の fuse.js は検索語が候補に部分一致するだけで score≈0 を返すため、
// score だけだと「宿泊4ダイブ」で「宿泊4ダイブツアー」も「前泊宿泊4ダイブツアー」も 100% になる。
// 候補側に余分な文字が多いほど一致度を下げるよう、正規化後の長さ比を掛けて補正する。
//   一致度 = (1 - score) × (検索語の長さ / 候補の長さ)
// 例: 「宿泊4ダイブ」(6) vs 「宿泊4ダイブツアー」(9) → 約0.67、「前泊宿泊4ダイブツアー」(11) → 約0.54。
// 完全一致なら長さ比=1・score=0 となり 1.0(=100%) を維持する。
export const calcDictionaryMatchRate = (
  score: number,
  normalizedQuery: string,
  normalizedTarget: string,
): number => {
  const base = 1 - score;
  if (!normalizedTarget) return base;
  const lengthRatio = Math.min(normalizedQuery.length / normalizedTarget.length, 1);
  return base * lengthRatio;
};

// 辞書のあいまい照合（クライアント側 fuse.js）
// search_csv_column を検索キーにして rows をあいまい検索し、
// スコア昇順（一致度が高い順）に候補を返す。
// 検索キーの値・検索クエリともに normalizeForDictionarySearch で正規化してから照合する。
export const dictionaryFuzzySearch = (
  rows: DictionaryRow[],
  searchCsvColumn: string,
  searchValue: string,
  threshold: number = DICTIONARY_FUSE_THRESHOLD,
): DictionaryMatchResult[] => {
  const normalizedQuery = normalizeForDictionarySearch(searchValue.trim());
  if (!normalizedQuery) return [];

  const fuse = new Fuse<DictionaryRow>(rows, {
    keys: [searchCsvColumn],
    includeScore: true,
    threshold,
    ignoreLocation: true,
    // 辞書側の値も正規化してから照合する（元の値は result.item にそのまま残る）。
    getFn: (row): string => normalizeForDictionarySearch(String(row[searchCsvColumn] ?? '')),
  });

  return (
    fuse
      .search(normalizedQuery)
      .map(result => {
        const score = result.score ?? 0;
        const normalizedTarget = normalizeForDictionarySearch(
          String(result.item[searchCsvColumn] ?? ''),
        );
        return {
          rowData: result.item,
          matchedValue: result.item[searchCsvColumn] ?? '',
          score,
          matchRate: calcDictionaryMatchRate(score, normalizedQuery, normalizedTarget),
        };
      })
      // 一致度（補正後）が高い順に並べる。
      .sort((a, b) => b.matchRate - a.matchRate)
  );
};
